import { useState, useMemo } from "react";
import { RouteName } from "constants/route";
import SideBarNav from "./SideBarNav";
import image from "assets/images/emun-taxi-logo-orange.png";
import { jwtDecode } from "jwt-decode";
import { FiActivity } from "react-icons/fi";
import {
  AiOutlineCalendar,
  AiOutlineDown,
  AiOutlineRight,
  AiOutlineTransaction,
} from "react-icons/ai";
import {
  MdGeneratingTokens,
  MdLocationPin,
  MdOutlineHeadset,
  MdOutlineHealthAndSafety,
  MdOutlinePassword,
  MdOutlineSos,
} from "react-icons/md";
import { TbUsers } from "react-icons/tb";
import { RxSpeakerLoud } from "react-icons/rx";
import { FaKey, FaQ } from "react-icons/fa6";
import {
  AdminPrivilege,
  DispatchAndSupperAdminPrivilege,
  DispatchPrivilege,
  DispatchWithPolicePrivilege,
  ERole,
  FinancePrivilege,
  FinanceWithPolicePrivilege,
} from "models/account";
import { FaCar, FaCity, FaUserFriends } from "react-icons/fa";
import { CiBookmark } from "react-icons/ci";
import { BiSolidReport } from "react-icons/bi";
import { GoVersions } from "react-icons/go";
import { IoIosSettings } from "react-icons/io";
import { FiSettings } from "react-icons/fi";
import OpenSideBarIcon from "./Icons/OpenSideBarIcon";
import CloseSideBarIcon from "./Icons/CloseSideBarIcon";
import { LogoutOutlined } from "@ant-design/icons";
import { Button, Modal } from "antd";
import { useNavigate } from "react-router-dom";
import { isNotPolice } from "helpers/privilege";

interface IProps {
  sideBarCollapse: boolean;
  toggleSideBarCollapse: () => void;
}

export default function SideBar({
  sideBarCollapse,
  toggleSideBarCollapse,
}: IProps) {
  const navigate = useNavigate();
  const [isWorkforceOpen, setIsWorkforceOpen] = useState<boolean>(false);
  const [isFleetsOpen, setIsFleetsOpen] = useState<boolean>(false);
  const [isDispatchOpen, setIsDispatchOpen] = useState<boolean>(false);
  const [isBroadcastOpen, setIsBroadcastOpen] = useState<boolean>(false);
  const [openLogoutModal, setOpenLogoutModal] = useState<boolean>(false);

  const role: ERole = useMemo(() => {
    const payload: any = jwtDecode(localStorage.getItem("token")!);
    return payload.role;
  }, []);

  const toggleFleets = () => {
    if (!sideBarCollapse) setIsFleetsOpen(!isFleetsOpen);
  };

  const toggleWorkforce = () => {
    if (!sideBarCollapse) setIsWorkforceOpen(!isWorkforceOpen);
  };

  const toggleDispatch = () => {
    if (!sideBarCollapse) setIsDispatchOpen(!isDispatchOpen);
  };

  const toggleBroadcast = () => {
    if (!sideBarCollapse) setIsBroadcastOpen(!isBroadcastOpen);
  };

  const handleShowLogoutModal = () => {
    setOpenLogoutModal(true);
  };
  const handleCloseLogoutModal = () => {
    setOpenLogoutModal(false);
  };
  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    navigate(RouteName.LOGIN, { replace: true });
  };

  return (
    <aside
      className={`fixed z-50 top-0 left-0 bg-white w-full duration-300 ${sideBarCollapse ? "max-w-20" : "max-w-260"
        } flex flex-col overflow-y-auto`}
      style={{ boxShadow: "0 0 15px 0 rgba(0,0,0,.05)", height: "100vh" }}
    >
      <div
        className={`w-full flex items-start ${sideBarCollapse
          ? "py-5 px-1 justify-center"
          : "py-5 px-4 justify-between"
          }`}
      >
        {!sideBarCollapse && (
          <img src={image} alt="logo" className="pl-1 grow max-w-20 w-full" />
        )}
        <button
          onClick={toggleSideBarCollapse}
          className="text-[#565656] rounded-md flex items-center justify-center hover:bg-gray-200 max-w-8 h-8 grow"
        >
          {sideBarCollapse ? <OpenSideBarIcon /> : <CloseSideBarIcon />}
        </button>
      </div>
      <div className="w-full pt-2 relative overflow-y-auto grow flex flex-col">
        {FinanceWithPolicePrivilege.includes(role) && (
          <div className="w-full px-1 mb-5">
            <button
              className={`flex w-full text-left items-center ${sideBarCollapse ? "justify-center" : "justify-start"
                }`}
              onClick={toggleWorkforce}
            >
              <TbUsers size={23} className="mr-2 ml-2" />
              {!sideBarCollapse && (
                <span className="flex w-full text-left items-center">
                  <span className="mx-5 text-base font-bold">Workforce</span>
                  <span className="ml-auto transition-transform duration-700 ease-in-out transform">
                    {isWorkforceOpen ? <AiOutlineDown /> : <AiOutlineRight />}
                  </span>
                </span>
              )}
            </button>
            <div
              className={`overflow-hidden transition-max-height duration-700 ease-in-out ${isWorkforceOpen ? "max-h-screen" : "max-h-0"
                }`}
            >
              {isWorkforceOpen && (
                <div>
                  {role === ERole.SUPER_ADMIN && (
                    <SideBarNav
                      sideBarCollapse={sideBarCollapse}
                      to={RouteName.ACCOUNT}
                      name="Manage Accounts"
                    />
                  )}

                  <SideBarNav
                    sideBarCollapse={sideBarCollapse}
                    to={
                      isNotPolice() ? RouteName.DRIVER : RouteName.DRIVER_SEARCH
                    }
                    name="Manage Drivers"
                  />
                  {FinancePrivilege.includes(role) && (
                    <SideBarNav
                      sideBarCollapse={sideBarCollapse}
                      to={RouteName.DRIVER_WALLET}
                      name="Driver Wallet"
                    />
                  )}
                  {FinancePrivilege.includes(role) && (
                    <SideBarNav
                      sideBarCollapse={sideBarCollapse}
                      to={RouteName.PASSENGER_WALLET}
                      name="Passenger Wallet"
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        )}
        {FinancePrivilege.includes(role) && (
          <div className="w-full px-1 mb-5">
            <button
              className={`flex w-full text-left items-center ${sideBarCollapse ? "justify-center" : "justify-start"
                }`}
              onClick={toggleFleets}
            >
              <AiOutlineCalendar size={23} className="mr-2 ml-2" />
              {!sideBarCollapse && (
                <span className="flex w-full text-left items-center">
                  <span className="mx-5 text-base font-bold">Fleets</span>
                  <span className="ml-auto transition-transform duration-700 ease-in-out transform">
                    {isFleetsOpen ? <AiOutlineDown /> : <AiOutlineRight />}
                  </span>
                </span>
              )}
            </button>
            <div
              className={`overflow-hidden transition-max-height duration-700 ease-in-out ${isFleetsOpen ? "max-h-screen" : "max-h-0"
                }`}
            >
              {isFleetsOpen && (
                <div>
                  {role === ERole.SUPER_ADMIN && (
                    <SideBarNav
                      sideBarCollapse={sideBarCollapse}
                      to={RouteName.FLEET_MODEL}
                      name="Fleet Models"
                    />
                  )}
                  <SideBarNav
                    sideBarCollapse={sideBarCollapse}
                    to={RouteName.FLEET}
                    name="Fleets"
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {DispatchPrivilege.includes(role) && (
          <div className="w-full px-1 mb-5">
            <button
              className={`flex w-full text-left items-center ${sideBarCollapse ? "justify-center" : "justify-start"
                }`}
              onClick={toggleDispatch}
            >
              <MdOutlineHeadset size={23} className="mr-2 ml-2" />
              {!sideBarCollapse && (
                <span className="flex w-full text-left items-center">
                  <span className="mx-5 text-base font-bold">Dispatcher</span>
                  <span className="ml-auto transition-transform duration-700 ease-in-out transform">
                    {isDispatchOpen ? <AiOutlineDown /> : <AiOutlineRight />}
                  </span>
                </span>
              )}
            </button>
            <div
              className={`overflow-hidden transition-max-height duration-700 ease-in-out ${isDispatchOpen ? "max-h-screen" : "max-h-0"
                }`}
            >
              {isDispatchOpen && (
                <div>
                  <SideBarNav
                    sideBarCollapse={sideBarCollapse}
                    to={RouteName.DASHBOARD}
                    name="Dashboard"
                  />
                  {DispatchAndSupperAdminPrivilege.includes(role) && (
                    <SideBarNav
                      sideBarCollapse={sideBarCollapse}
                      to={RouteName.DISPATCHER}
                      name="Dispatch"
                    />
                  )}
                  <SideBarNav
                    sideBarCollapse={sideBarCollapse}
                    icon={<FiSettings size={23} />}
                    to={RouteName.STUCK_DRIVERS}
                    name="Stuck Drivers"
                  />
                  <SideBarNav
                    sideBarCollapse={sideBarCollapse}
                    icon={<MdOutlineHeadset size={23} />}
                    to={RouteName.PENALTY_MANAGEMENT}
                    name="Driver Penalties"
                  />
                  <SideBarNav
                    sideBarCollapse={sideBarCollapse}
                    icon={<MdGeneratingTokens size={23} />}
                    to={RouteName.LOTTERY_MANAGEMENT}
                    name="Lottery Management"
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {AdminPrivilege.includes(role) && (
          <div className="w-full px-1 mb-4">
            <button
              className={`flex w-full text-left items-center ${sideBarCollapse ? "justify-center" : "justify-start"
                }`}
              onClick={toggleBroadcast}
            >
              <RxSpeakerLoud size={23} className="mr-2 ml-2" />
              {!sideBarCollapse && (
                <span className="flex w-full text-left items-center">
                  <span className="mx-5 text-base font-bold">Broadcast</span>
                  <span className="ml-auto transition-transform duration-700 ease-in-out transform">
                    {isBroadcastOpen ? <AiOutlineDown /> : <AiOutlineRight />}
                  </span>
                </span>
              )}
            </button>
            <div
              className={`overflow-hidden transition-max-height duration-700 ease-in-out ${isBroadcastOpen ? "max-h-screen" : "max-h-0"
                }`}
            >
              {isBroadcastOpen && (
                <div>
                  <SideBarNav
                    sideBarCollapse={sideBarCollapse}
                    to={RouteName.NEWS_BROADCAST}
                    name="New Broadcast"
                  />
                  <SideBarNav
                    sideBarCollapse={sideBarCollapse}
                    to={RouteName.NEWS}
                    name="News"
                  />
                </div>
              )}
            </div>
          </div>
        )}
        {AdminPrivilege.includes(role) && (
          <>
            <SideBarNav
              sideBarCollapse={sideBarCollapse}
              icon={<FaCity size={23} />}
              to={RouteName.CITY}
              name="Cities"
            />
          </>
        )}
        {/* {AdminPrivilege.includes(role) && (
          <SideBarNav
            sideBarCollapse={sideBarCollapse}
            icon={<CiGift size={23} />}
            to={RouteName.PROMO_CODE}
            name="Promo Code"
          />
        )} */}
        {role === ERole.SUPER_ADMIN && (
          <SideBarNav
            sideBarCollapse={sideBarCollapse}
            icon={<FiActivity size={23} />}
            to={RouteName.ACTIVITY_LOG}
            name="Activity Log"
          />
        )}
        {AdminPrivilege.includes(role) && (
          <SideBarNav
            sideBarCollapse={sideBarCollapse}
            icon={<MdGeneratingTokens size={23} />}
            to={RouteName.RATING}
            name="Ratings"
          />
        )}
        {AdminPrivilege.includes(role) && (
          <SideBarNav
            sideBarCollapse={sideBarCollapse}
            icon={<MdLocationPin size={23} />}
            to={RouteName.PLACE}
            name="Location"
          />
        )}
        {DispatchPrivilege.includes(role) && (
          <SideBarNav
            sideBarCollapse={sideBarCollapse}
            icon={<FaCar size={23} />}
            to={RouteName.TRIP}
            name="Trips"
          />
        )}
        {DispatchPrivilege.includes(role) && (
          <SideBarNav
            sideBarCollapse={sideBarCollapse}
            icon={<CiBookmark size={23} />}
            to={RouteName.TRIP_REQUEST}
            name="Trip Requests"
          />
        )}
        {/* {DispatchPrivilege.includes(role) && (
          <SideBarNav
            sideBarCollapse={sideBarCollapse}
            icon={<FaCar size={23} />}
            to={RouteName.CARPOOLING}
            name="Carpooling"
          />
        )} */}
        {DispatchWithPolicePrivilege.includes(role) && (
          <SideBarNav
            sideBarCollapse={sideBarCollapse}
            icon={<MdOutlineSos size={23} />}
            to={RouteName.SOS}
            name="Sos"
          />
        )}
        {role === ERole.CASHER && (
          <SideBarNav
            sideBarCollapse={sideBarCollapse}
            icon={<AiOutlineTransaction size={23} />}
            to={RouteName.FINANCE_DASHBOARD}
            name="Finance Dashboard"
          />
        )}
        {AdminPrivilege.includes(role) && (
          <SideBarNav
            sideBarCollapse={sideBarCollapse}
            icon={<AiOutlineTransaction size={23} />}
            to={RouteName.FINANCE}
            name="Finances"
          />
        )}
        {AdminPrivilege.includes(role) && (
          <SideBarNav
            sideBarCollapse={sideBarCollapse}
            icon={<BiSolidReport size={23} />}
            to={RouteName.REPORT_REQUEST}
            name="Report Request"
          />
        )}
        {AdminPrivilege.includes(role) && (
          <SideBarNav
            sideBarCollapse={sideBarCollapse}
            icon={<FaUserFriends size={23} />}
            to={RouteName.PASSENGER}
            name="Passengers"
          />
        )}
        {role === ERole.SUPER_ADMIN && (
          <SideBarNav
            sideBarCollapse={sideBarCollapse}
            icon={<GoVersions size={23} />}
            to={RouteName.APP_VERSION}
            name="App Version"
          />
        )}
        {AdminPrivilege.includes(role) && (
          <SideBarNav
            sideBarCollapse={sideBarCollapse}
            icon={<FaKey size={23} />}
            to={RouteName.OTP}
            name="Otp"
          />
        )}
        {AdminPrivilege.includes(role) && (
          <>
            <SideBarNav
              sideBarCollapse={sideBarCollapse}
              icon={<FaQ size={23} />}
              to={RouteName.FAQ}
              name="FAQS"
            />
          </>
        )}
        {role === ERole.SUPER_ADMIN && (
          <SideBarNav
            sideBarCollapse={sideBarCollapse}
            icon={<IoIosSettings size={23} />}
            to={RouteName.SETTING}
            name="Settings"
          />
        )}
        <div className="w-full relative overflow-visible mb-1 grow flex flex-col justify-end py-10px">
          <SideBarNav
            sideBarCollapse={sideBarCollapse}
            icon={<MdOutlinePassword size={23} />}
            to={RouteName.CHANGE_PASSWORD}
            name="Change Password"
          />
          <div className="w-full px-4">
            <button
              onClick={handleShowLogoutModal}
              className="transition-all duration-300 pl-4 text-base w-full flex items-center justify-between hover:pl-6 text-red-600 font-semibold"
            >
              <div className="w-full flex items-center gap-x-5">
                <LogoutOutlined />
                <p className="text-ellipsis text-nowrap overflow-hidden">
                  Logout
                </p>
              </div>
            </button>
          </div>
        </div>
      </div>
      <Modal
        open={openLogoutModal}
        footer={null}
        onCancel={handleCloseLogoutModal}
        title="Logout"
      >
        <div className="w-full">
          <div className="w-full flex flex-col gap-y-4">
            <p className="text-base font-normal text-black whitespace-nowrap">
              Are you sure you want to logout?
            </p>
            <div className="w-full flex items-center justify-end gap-x-4">
              <Button
                onClick={handleCloseLogoutModal}
                className="bg-gray-900 "
                type="primary"
              >
                Cancel
              </Button>

              <Button onClick={handleLogout} danger type="primary">
                Logout
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    </aside>
  );
}
